import * as React from 'react';
import {
  COST_BEARER_OWN,
  COST_BEARER_RENTER,
  Item,
  MaintenanceHistory,
  MaintenanceHistoryItemEntry
} from '../api/dtos/dtos';
import * as dateUtils from '../utils/date-utils';
import {splitIntoChunks} from '../utils/utils';
import {groupNumber} from "../utils/number-utils";
import locales from "../locales";

interface PrintRentalPageProps {
  item: Item,
  historyItem: MaintenanceHistory;
}

/**
 * Avoid collapsing grid layout content
 */
export function noCollapse(content?: React.ReactNode): React.ReactNode {
  return content ? content : <span>&nbsp;</span>;
}

function calculateSum(a: number | null, b: number | null): number {
  return (a ?? 0) * (b ?? 0)
}

function getCostBearerLabel(costBearer: string | null): string {
  if (costBearer === COST_BEARER_RENTER) {
    return locales.cost_renter_short;
  } else if (costBearer === COST_BEARER_OWN) {
    return locales.cost_own_short;
  } else {
    return "";
  }
}

function getContactLabel(historyItem: MaintenanceHistory): string {
  const {contactName, contactPhone} = historyItem;
  let label = '';
  if (contactName) {
    label = label.concat(`${contactName}, `);
  }
  if (contactPhone) {
    label = label.concat(`${locales.tel_short} ${contactPhone}`);
  }
  return label;
}

class PrintMaintenanceSheetPage extends React.Component<PrintRentalPageProps, {}> {

  public render(): React.ReactNode {
    const {historyItem} = this.props;
    const entries = historyItem.entries || [];
    const chunks: MaintenanceHistoryItemEntry[][] = splitIntoChunks(entries, 10);

    if (chunks.length === 0) {
      return this.renderPage([], 0);
    } else {
      return chunks.map((items, idx) => this.renderPage(items, idx));
    }
  }


  private renderPage = (entries: MaintenanceHistoryItemEntry[], pageIdx: number) => {
    const {historyItem, item} = this.props;
    const {date, travelDistance, travelCost, repairTime, repairCost, costBearer, totalCost} = historyItem;
    const rightAlignStyle: React.CSSProperties = {textAlign: 'right'};
    const centerAlignStyle: React.CSSProperties = {textAlign: 'center'};

    const travelSum = calculateSum(travelCost, travelDistance);
    const repairSum = calculateSum(repairCost, repairTime);
    const entriesSum = entries.map(e => e.cost * e.amount).reduce((a, b) => a + b, 0);

    return (
      <div className="page" key={String(pageIdx)}>

        <p className="label" style={rightAlignStyle}>Azonosító: <b>{historyItem.id}</b></p>

        <h1>SZERVÍZ MUNKALAP</h1>

        <p>
          <span className="label">Szervíz időpontja:</span>
          <span className="input large">{dateUtils.formatDateOrNull(date)}</span>
          <span className="input large">{dateUtils.formatTimeOrNull(date)}</span>
        </p>

        <p className="label">Munkavégzés helye:</p>
        <p className="input">{noCollapse(historyItem.site?.address)}</p>

        <div className="grid">
          <div className="elem">
            <p className="label">Ügyfél neve:</p>
            <p className="input">{noCollapse(historyItem.client?.name)}</p>
          </div>
          <div className="elem">
            <p className="label">Kapcsolattartó:</p>
            <p className="input">{noCollapse(getContactLabel(historyItem))}</p>
          </div>
          <div className="elem">
            <p className="label">Költségviselő:</p>
            <p className="input">{noCollapse(getCostBearerLabel(costBearer))}</p>
          </div>
          <div className="elem">
            <p className="label">Munkavégző(k) neve:</p>
            <p className="input">{noCollapse(historyItem.workerNames)}</p>
          </div>
        </div>

        <div className="grid">
          <div className="elem">
            <p className="label">Gép:</p>
            <p className="input">{noCollapse(item.name)}</p>
          </div>
          <div className="elem">
            <p className="label">Gép típusa:</p>
            <p className="input">{noCollapse(item.category)}</p>
          </div>
          <div className="elem">
            <p className="label">Gép gyári száma:</p>
            <p className="input">{noCollapse(item.serial)}</p>
          </div>
          <div className="elem">
            <p className="label">Leltári szám:</p>
            <p className="input">{noCollapse(item.inventoryNumber)}</p>
          </div>
          {historyItem.operationHours && (
            <div className="elem">
              <p className="label">Üzemóraállás:</p>
              <p className="input">{noCollapse(historyItem.operationHours)} óra</p>
            </div>
          )}
        </div>

        <p className="label">Hibajelenség részletes leírása:</p>
        <p className="input">{noCollapse(historyItem.errorDescription)}</p>

        <p className="label">Elvégzett munka:</p>
        <p className="input">{noCollapse(historyItem.workDescription)}</p>

        {entries && entries.length > 0 && <>
            <p className="label">Anyagfelhasználás:</p>
            <table style={{marginTop: '2pt'}}>
                <thead>
                <tr>
                    <th>Cikkszám</th>
                    <th>Megnevezés</th>
                    <th>Mennyiség (db)</th>
                    <th>Egységár (Ft)</th>
                    <th>Összesen (Ft)</th>
                </tr>
                </thead>
                <tbody>
                {entries.map((entry, index) => {
                  const total = entry.amount * entry.cost;
                  return (
                    <tr key={index}>
                      <td style={centerAlignStyle}>{entry.inventoryNumber}</td>
                      <td style={centerAlignStyle}>{entry.name}</td>
                      <td style={centerAlignStyle}>{groupNumber(entry.amount)}</td>
                      <td style={rightAlignStyle}>{groupNumber(entry.cost)}</td>
                      <td style={rightAlignStyle}>{groupNumber(total)}</td>
                    </tr>
                  );
                })}
                </tbody>
            </table>
            <p style={rightAlignStyle}>
                <span className="label">Összesen:</span>
                <span className="input">{groupNumber(entriesSum)} Ft</span>
            </p>
        </>}

        {(travelSum > 0 || repairSum > 0) && <>
            <p className="label">Munkadíj:</p>
            <table style={{marginTop: '2pt'}}>
                <tbody>
                {travelSum > 0 && <tr>
                    <td>Kiszállás</td>
                    <td style={rightAlignStyle}>{groupNumber(travelDistance)} km</td>
                    <td style={rightAlignStyle}>{groupNumber(travelCost)} Ft</td>
                    <td style={rightAlignStyle}>{groupNumber(travelSum)} Ft</td>
                </tr>}
                {repairSum > 0 && <tr>
                    <td>Javítás</td>
                    <td style={rightAlignStyle}>{groupNumber(repairTime)} óra</td>
                    <td style={rightAlignStyle}>{groupNumber(repairCost)} Ft</td>
                    <td style={rightAlignStyle}>{groupNumber(repairSum)} Ft</td>
                </tr>}
                </tbody>
            </table>
            <p style={rightAlignStyle}>
                <span className="label">Összesen:</span>
                <span className="input">{groupNumber(travelSum + repairSum)} Ft</span>
            </p>
        </>}

        <p>A munka elvégzését és a fent leírtak valódiságát igazolom.</p>

        <div style={{marginTop: 24}} className="grid">
          <p>
            <span className="label">Kelt:</span>
            <span className="input large">Budapest</span>
            <span className="input large">{dateUtils.formatDateOrNull(date)}</span>
          </p>
          <p style={rightAlignStyle}>
            {entries.length === 0 &&
                <span className="label">Munkadíj összesen:</span>
            }
            {entries.length > 0 &&
                <span className="label">Anyagfelhasználás és munkadíj összesen:</span>
            }
            <span className="input large">{groupNumber(totalCost)} Ft</span>
          </p>
        </div>

        <div className="footer">
          <p>Megrendelő / átvevő</p>
          <p>Átadó</p>
        </div>
      </div>
    );
  };
}

export default PrintMaintenanceSheetPage;



// WEBPACK FOOTER //
// ./src/print/PrintMaintenanceSheetPage.tsx